import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import StatusLabel from '@/components/StatusLabel';
import CompanyTaxInfoCard from '@/components/company/CompanyTaxInfoCard';
import { companyService } from '@/data/companyService';
import { Company } from '@/types/Company.interface';
import { useUserViewMode } from '@/hooks/useUserViewMode';

/**
 * One company: its overview and its B2B tax setup. Reached from the branch
 * details (company name) and, for the system admin, from the companies
 * list. The backend scopes `GET /companies/:id` to the caller's own company.
 */
const CompanyDetails = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const { isSuperAdmin } = useUserViewMode();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setCompany(await companyService.getById(Number(companyId)));
    } catch (error) {
      console.error('Error fetching company details:', error);
      setCompany(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (failed || !company) {
    return (
      <Container>
        <Typography variant="h4" gutterBottom color="error">
          {t('company.not.found')}
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('company.details')}</title>
      </Helmet>
      <PageTitleWrapper>
        <Box display="flex" alignItems="center">
          {isSuperAdmin && (
            <Tooltip arrow placement="top" title={t('back')}>
              <IconButton
                component={Link}
                to="/management/companies"
                color="primary"
                sx={{ p: 1, mr: 2 }}
              >
                <ArrowBackTwoToneIcon />
              </IconButton>
            </Tooltip>
          )}
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              {t('company.details')}
            </Typography>
            <Typography variant="subtitle2">
              {t('company.details.description')}
            </Typography>
          </Box>
        </Box>
      </PageTitleWrapper>

      <Container disableGutters maxWidth={false} sx={{ maxWidth: '90%' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ borderRadius: 2, p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('name')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {company.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('status')}
                  </Typography>
                  <StatusLabel status={company.status} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('company.details.branches')}
                  </Typography>
                  <Typography variant="body1">
                    {company.numberOfBranchesUnderThisCompany ?? '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <CompanyTaxInfoCard company={company} onSaved={setCompany} />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default CompanyDetails;
